import { ElementType } from 'src/app/_objects/element-type';
import { ReportProperties } from 'src/app/_objects/report-properties';
import {
  ColumnList,
  DisplayTableColumn,
  ListItem,
  Options,
  RowList,
  TemplateDataPlateImageInfo,
  TemplateDataPlateText,
  TemplateImage,
  TemplateSummaryTable,
  TemplateText,
} from 'src/app/_objects/report-template';
import { LangUpdateService } from 'src/app/_services/lang-update.service';
import {
  PropertyPreviewMetadata,
  ReportTemplateDesignService,
  TypeOfJSON,
  UpdateMetadata,
} from 'src/app/_services/report-template-design.service';
import { REPORT_TEMPLATE___APPLICATION_TYPE, StorageService } from 'src/app/_services/storage.service';

import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { ENTER } from '@angular/cdk/keycodes';
import { Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { UntypedFormControl, ValidatorFn } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatAccordion, MatExpansionPanel } from '@angular/material/expansion';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';

import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CommonDeleteDialogComponent } from 'src/app/common-dialog/common-delete-dialog/common-delete-dialog.component';
import { PriorityList } from '../../_objects/report-template';
import {
  ERROR_MESSAGE,
  HEADER_DYNAMIC_SELECT_ITEM,
  PICTURES_DYNAMIC_SELECT_ITEM,
  PROP_DYNAMIC_DATEFORMAT,
  PROP_DYNAMIC_PAGEFORMAT,
  SUBTOTAL_DYNAMIC_SELECT_ITEM,
  SUMMARY_DYNAMIC_SELECT_ITEM,
  TEXT_TYPES,
} from '../../app.constants';
import { CustomValidators } from '../../helpers/custom-validator';

@Component({
    selector: 'app-property-editor',
    templateUrl: './property-preview-editor.component.html',
    styleUrls: ['./property-preview-editor.component.scss'],
    standalone: false
})
export class PropertyEditorComponent implements OnInit {
  @ViewChild('dragDropTable') public dragDropTable: MatTable<Options>;
  @ViewChild('priorityItemspTable') public priorityItemspTable: MatTable<PriorityList>;
  @ViewChild('tableColumnAccordion') public tableColumnAccordion?: MatAccordion;
  @ViewChildren('tableColumnAccordionPanels') public tableColumnAccordionPanels?: QueryList<MatExpansionPanel>;
  @Input() public propId: string;
  @Input() public metadata: PropertyPreviewMetadata;
  @Input() public isCommon: boolean;
  @Output() public changeValue: EventEmitter<UpdateMetadata> = new EventEmitter<UpdateMetadata>();

  public readonly tableColumnPropertyPanelIdPrefix: string = 'table-column-property-panel-';
  public textField: UntypedFormControl;
  public numberField: UntypedFormControl;
  public integerField: UntypedFormControl;
  public textAreaField: UntypedFormControl;
  public jsonField: UntypedFormControl;
  public colorField: UntypedFormControl;
  public multiField: Options[];
  public selectField: UntypedFormControl;
  public checkBoxField: UntypedFormControl;
  public listDataFieldsForMapping: Array<{ label: string; value: string }> = [];
  public isMultiEdit = true;
  public isOptionsError = false;
  public oldValidValue: TypeOfJSON;
  public propertyTableColumns = ['propertyValue'];

  public labelMaxLength = 150;
  public maxVeLabelLength = 500; // This should match the ve label limit set in IPM DB, otherwise the VE event won't save properly
  public optionDataSource: MatTableDataSource<Options> = new MatTableDataSource();
  public priorityItemsDataSource: MatTableDataSource<PriorityList> = new MatTableDataSource();
  public typeValue: string;
  public inputTypeValue: string;
  public dynamicIdValue: string;
  public maxLengthValue: number;
  public listItem: ListItem[] = [];
  public tableColumnHeader: ColumnList[] = [];
  public tableRowHeader: RowList[] = [];
  public priorityItems: PriorityList[] = [];
  public title: TemplateText;
  public table: TemplateSummaryTable;
  public tableColumns: DisplayTableColumn[] = [];
  public dropDownOptionItems: Options[] = [];
  public imageInfo: TemplateDataPlateImageInfo;
  public imageInfoTitle: TemplateDataPlateText;
  public imageInfoImage: TemplateImage;
  public readonly separatorKeysCodes: number[] = [ENTER];
  public isVisol: boolean;
  public errorMessage = ERROR_MESSAGE;

  public constructor(
    private designService: ReportTemplateDesignService,
    private storageService: StorageService,
    private translate: TranslateService,
    private langService: LangUpdateService,
    private dialog: MatDialog
  ) {
    this.langService.prepare(this, this.translate);
  }

  @Input() public set type(type: string) {
    this.typeValue = type;
    this.onChangeType(this.typeValue);
  }
  @Input() public set inputType(inputType: string) {
    this.inputTypeValue = inputType;
    this.onChangeInputType(inputType);
  }
  @Input() public set dynamicId(dynamicId: string) {
    this.dynamicIdValue = dynamicId;
    this.onChangeDynamicId(dynamicId);
  }
  @Input() public set maxLength(maxLength: number) {
    this.maxLengthValue = maxLength;
  }

  public ngOnInit() {
    let validators = new Array<ValidatorFn>();
    this.oldValidValue = this.metadata.value;
    const appType = this.storageService.fetchApplicationType(REPORT_TEMPLATE___APPLICATION_TYPE);
    if (appType) {
      this.isVisol = appType.IsVisol;
    }

    const metadataname: string = this.getBottomMetadataName();

    if (metadataname === 'columns') {
      if (this.metadata.isFieldType('TableColumn')) {
        this.tableColumns =
          typeof this.metadata.value === 'string'
            ? this.textInfoTypeShaping(JSON.parse(this.metadata.value))
            : this.textInfoTypeShaping(this.metadata.value);
      } else {
        this.listItem = typeof this.metadata.value === 'string' ? JSON.parse(this.metadata.value) : (this.metadata.value as ListItem[]);
        this.listDataFieldsForMapping = this.designService.getMeasurementPointDataFields();
        let type: string;
        const selectedElemProps: ReportProperties[] = this.designService.getSelectedElementProperties();
        selectedElemProps.forEach((reportProperties: ReportProperties) => {
          type = this.getPanelType(reportProperties.getPropOrElemType());
        });
        this.metadata.selectItems = this.designService.getReportDynamicDataFields(type);
      }
    } else if (metadataname === 'captionsList') {
      this.listItem = typeof this.metadata.value === 'string' ? JSON.parse(this.metadata.value) : (this.metadata.value as ListItem[]);
      this.listDataFieldsForMapping = this.designService.getVisualEventDataFields();
    } else if (metadataname === 'itemDataSource') {
      this.metadata.selectItems = this.designService.getVisualEventDataFields();
    } else if (metadataname === 'columnList') {
      this.tableColumnHeader =
        typeof this.metadata.value === 'string' ? (JSON.parse(this.metadata.value) as ColumnList[]) : (this.metadata.value as ColumnList[]);
    } else if (metadataname === 'rowList') {
      this.tableRowHeader =
        typeof this.metadata.value === 'string' ? (JSON.parse(this.metadata.value) as RowList[]) : (this.metadata.value as RowList[]);
    } else if (metadataname === 'priorityList') {
      this.priorityItems =
        typeof this.metadata.value === 'string'
          ? (JSON.parse(this.metadata.value) as PriorityList[])
          : (this.metadata.value as PriorityList[]);
      this.priorityItemsDataSource.data = this.priorityItems;
    } else if (metadataname === 'title') {
      this.title =
        typeof this.metadata.value === 'string'
          ? (this.tryParseJson(this.metadata.value) as TemplateText)
          : (this.metadata.value as TemplateText);
    } else if (metadataname === 'table') {
      this.table =
        typeof this.metadata.value === 'string'
          ? (this.tryParseJson(this.metadata.value) as TemplateSummaryTable)
          : (this.metadata.value as TemplateSummaryTable);
    } else if (this.metadata.isFieldType('DropDownOptionItems') && metadataname === 'options') {
      this.dropDownOptionItems =
        typeof this.metadata.value === 'string' ? (JSON.parse(this.metadata.value) as Options[]) : (this.metadata.value as Options[]);
    } else if (metadataname === 'imageInfo') {
      this.imageInfo =
        typeof this.metadata.value === 'string'
          ? (this.tryParseJson(this.metadata.value) as TemplateDataPlateImageInfo)
          : (this.metadata.value as TemplateDataPlateImageInfo);
      console.log({
        WatchImageInfo: 'This is Test',
        metadata: this.metadata,
        imageInfo: this.imageInfo,
      });
    } else if (metadataname === 'title' && this.metadata.parentName === 'imageInfo') {
      this.imageInfoTitle =
        typeof this.metadata.value === 'string'
          ? (this.tryParseJson(this.metadata.value) as TemplateDataPlateText)
          : (this.metadata.value as TemplateDataPlateText);
      console.log({
        WatchImageInfoTitle: 'This is Test',
        metadata: this.metadata,
        imageInfoTitle: this.imageInfoTitle,
      });
      validators = validators.concat(this.designService.validatorJSON());
      this.jsonField = new UntypedFormControl('', validators);
      this.jsonField.setValue(this.imageInfoTitle.width);

      validators = validators.concat(this.designService.validatorJSON());
      this.jsonField = new UntypedFormControl('', validators);
      this.jsonField.setValue(this.imageInfoTitle.height);
    } else if (metadataname === 'image' && this.metadata.parentName === 'imageInfo') {
      this.imageInfoImage =
        typeof this.metadata.value === 'string'
          ? (this.tryParseJson(this.metadata.value) as TemplateImage)
          : (this.metadata.value as TemplateImage);
      console.log({
        WatchImageInfoImage: 'This is Test',
        metadata: this.metadata,
        imageInfoImage: this.imageInfoImage,
      });
      validators = validators.concat(this.designService.validatorJSON());
      this.jsonField = new UntypedFormControl('', validators);
      this.jsonField.setValue(this.imageInfoImage.width);
    }

    if (this.metadata.isRequired) {
      validators.push(CustomValidators.requiredTrimmed());
    }

    if (this.metadata.isFieldType('Text')) {
      this.textField = new UntypedFormControl('', this.designService.validatorText());
      this.textField.setValue(this.metadata.value);
    } else if (this.metadata.isFieldType('Number')) {
      validators = validators.concat(this.designService.validatorNumber());
      this.numberField = new UntypedFormControl('', validators);
      this.numberField.setValue(this.metadata.value);
    } else if (this.metadata.isFieldType('Color')) {
      this.colorField = new UntypedFormControl('', this.designService.validatorColor());
      this.colorField.setValue(this.metadata.value);
    } else if (this.metadata.isFieldType('Integer')) {
      validators = validators.concat(this.designService.validatorInteger());
      this.integerField = new UntypedFormControl('', validators);
      this.integerField.setValue(this.metadata.value);
    } else if (this.metadata.isFieldType('TextArea')) {
      this.textAreaField = new UntypedFormControl('', this.designService.validatorTextArea());
      this.textAreaField.setValue(this.metadata.value);
    } else if (this.metadata.isFieldType('JSON') && metadataname !== 'title') {
      validators = validators.concat(this.designService.validatorJSON());
      this.jsonField = new UntypedFormControl('', validators);
      this.jsonField.setValue(this.metadata.value);
      console.log({
        WatchJson: 'This is Test',
        jsonField: this.jsonField,
      });
    } else if (this.metadata.isFieldType('Multi')) {
      validators = validators.concat(this.designService.validatorMulti());
      this.jsonField = new UntypedFormControl('', validators);
      this.jsonField.setValue(this.metadata.value);
      this.multiField = JSON.parse(this.metadata.value as string) as Options[];
      this.optionDataSource.data = this.multiField;
    } else if (this.metadata.isFieldType('Select')) {
      this.selectField = new UntypedFormControl('', validators);
      // Select要素（ドロップダウンリスト等）だけ、HTML側のvalue属性をオートバインディングする書き方にしても
      // 初期値が画面に表示されない現象が起きるため、setValue()を用いて値を設定する。
      this.selectField.setValue(this.metadata.value);
      if (metadataname === 'src') {
        this.metadata.selectItems = this.designService.getReportTemplateImages();
      }
      if (metadataname === 'dynamicDataField') {
        if (this.isVisol) {
          return false;
        }
        // TODO: Remove hard coded value once data types are stored in BE
        if (!(this.metadata.selectItems.length > 0 && this.metadata.selectItems[0].label === 'Visual Events')) {
          this.metadata.selectItems = this.designService.getReportDynamicDataFields(this.type);
        }
      }

      if (metadataname === 'dynamicId') {
        if (!this.isVisol) {
          return false;
        }

        let type: string;
        const selectedElemProps: ReportProperties[] = this.designService.getSelectedElementProperties();
        selectedElemProps.forEach((reportProperties: ReportProperties) => {
          type = this.getPanelType(reportProperties.getPropOrElemType());
        });

        this.metadata.selectItems = this.designService.getReportDynamicDataFields(type);
      }
      if (metadataname === 'stageId') {
        this.metadata.selectItems = this.designService.getStageName();
      }
    } else if (this.metadata.isFieldType('CheckBox')) {
      validators = validators.concat(this.designService.validatorCheckBox());
      this.checkBoxField = new UntypedFormControl('');
      this.checkBoxField.setValue(this.metadata.value);
    }
    this.onChangeType(this.typeValue);
    this.onChangeInputType(this.inputTypeValue);
    this.onChangeDynamicId(this.dynamicIdValue);
    this.onChangeAtSelect();
  }

  public textInfoTypeShaping(tableColumn: any): DisplayTableColumn[] {
    return tableColumn as DisplayTableColumn[];
  }

  public onChangeAtText() {
    this.changeValue.emit({
      id: this.propId,
      name: this.metadata.name,
      parentName: this.metadata.parentName,
      value: this.textField.value,
      oldValue: this.oldValidValue,
    });

    this.oldValidValue = this.textField.value;
  }

  public onChangeAtColor(color): void {
    if (this.oldValidValue !== color) {
      this.changeValue.emit({
        id: this.propId,
        name: this.metadata.name,
        parentName: this.metadata.parentName,
        value: color,
        oldValue: this.oldValidValue,
      });
      this.oldValidValue = color;
    }
  }

  public onChangeAtNumber() {
    if (this.numberField.hasError('required') || this.numberField.hasError('pattern')) {
      this.changeValue.emit({
        id: this.propId,
        name: this.metadata.name,
        parentName: this.metadata.parentName,
        value: this.numberField.value,
        oldValue: this.oldValidValue,
      });

      return;
    }

    this.changeValue.emit({
      id: this.propId,
      name: this.metadata.name,
      parentName: this.metadata.parentName,
      value: Number(this.numberField.value),
      oldValue: this.oldValidValue,
    });

    this.oldValidValue = Number(this.numberField.value);
  }

  public onChangeAtInteger() {
    if (this.integerField.hasError('required') || this.integerField.hasError('pattern')) {
      this.changeValue.emit({
        id: this.propId,
        name: this.metadata.name,
        parentName: this.metadata.parentName,
        value: this.integerField.value,
        oldValue: this.oldValidValue,
      });

      return;
    }

    this.changeValue.emit({
      id: this.propId,
      name: this.metadata.name,
      parentName: this.metadata.parentName,
      value: Number(this.integerField.value),
      oldValue: this.oldValidValue,
    });

    this.oldValidValue = Number(this.integerField.value);
  }

  public onChangeAtTextArea() {
    this.changeValue.emit({
      id: this.propId,
      name: this.metadata.name,
      parentName: this.metadata.parentName,
      value: this.textAreaField.value,
      oldValue: this.oldValidValue,
    });

    this.oldValidValue = this.textAreaField.value;
  }

  public onChangeAtJSON(event: Event) {
    let tmpValue;
    try {
      tmpValue = JSON.stringify(JSON.parse(this.jsonField.value), undefined, 4);
    } catch (error) {
      tmpValue = this.jsonField.value;
    }

    if (this.oldValidValue !== tmpValue) {
      this.jsonField.setValue(tmpValue, { emitEvent: false });

      this.changeValue.emit({
        id: this.propId,
        name: this.metadata.name,
        parentName: this.metadata.parentName,
        value: tmpValue,
        oldValue: this.oldValidValue,
      });

      this.oldValidValue = tmpValue;
    }
  }

  public onChangeAtMulti() {
    this.optionDataSource.data = this.multiField;
    const tmpValue = JSON.stringify(this.multiField, undefined, 4);
    if (this.oldValidValue !== tmpValue) {
      this.changeValue.emit({
        id: this.propId,
        name: this.metadata.name,
        parentName: this.metadata.parentName,
        value: tmpValue,
        oldValue: this.oldValidValue,
      });

      this.oldValidValue = tmpValue;
    }
    console.log('optionDataSource', this.optionDataSource);
  }

  public onChangeAtList() {
    this.metadata.value = this.listItem;
    const tmpValue = JSON.stringify(this.listItem, undefined, 4);
    if (this.oldValidValue !== tmpValue) {
      this.changeValue.emit({
        id: this.propId,
        name: this.metadata.name,
        parentName: this.metadata.parentName,
        value: this.listItem,
        oldValue: this.oldValidValue,
      });
      this.oldValidValue = tmpValue;
    }
  }

  public onChangeAtTableHeader() {
    const updatedHeaders = this.metadata.name === 'rowList' ? this.tableRowHeader : this.tableColumnHeader;
    this.metadata.value = updatedHeaders;
    const tmpValue = JSON.stringify(updatedHeaders, undefined, 4);
    if (this.oldValidValue !== tmpValue) {
      this.changeValue.emit({
        id: this.propId,
        name: this.metadata.name,
        parentName: this.metadata.parentName,
        value: updatedHeaders,
        oldValue: this.oldValidValue,
      });
      this.oldValidValue = tmpValue;
    }
  }

  public updateTextComponent(textsInfo: TemplateText, event: TemplateText) {
    textsInfo = {
      ...event,
      validation: event.validation ?? { required: false, maxLength: 100 }};
    this.onChangeAtTableColumn();
  }

  public onChangeAtTableColumn() {
    const updatedColumns = this.metadata.name === 'columns' ? this.tableColumns : null;
    this.metadata.value = updatedColumns;
    const tmpValue = JSON.stringify(updatedColumns, undefined, 4);
    if (this.oldValidValue !== tmpValue) {
      this.changeValue.emit({
        id: this.propId,
        name: this.metadata.name,
        parentName: this.metadata.parentName,
        value: updatedColumns,
        oldValue: this.oldValidValue,
      });
      this.oldValidValue = tmpValue;
    }
  }

  public addText(index: number) {
    const updateColumn = this.metadata.name === 'columns' ? this.tableColumns : null;
    if (updateColumn) {
      const templateText: TemplateText = new TemplateText();
      templateText.type = 'static';
      templateText.text = '';
      templateText.inputType = 'text';
      templateText.validation = { required: false, maxLength: 100 };
      templateText.multiSelectDelimiter = 'comma';
      templateText.isTableText = true;
      templateText.options = null;
      this.tableColumns[index].textsInfo.push(templateText);
      this.onChangeAtTableColumn();
    }
  }
  public deleteText(index1: number, index2: number) {
    this.tableColumns[index1].textsInfo.splice(index2, 1);
    this.onChangeAtTableColumn();
  }

  public addObject() {
    if (!this.multiField) {
      this.multiField = [];
    }
    if (this.multiField.length < 100) {
      this.multiField.push({ label: '', value: '' });
      this.onChangeAtMulti();
      this.isOptionsError = false;
    }
  }
  public deleteObject(index: number) {
    this.multiField.splice(index, 1);
    this.onChangeAtMulti();
    if (this.multiField.length === 0) {
      this.isOptionsError = true;
    }
  }
  public isRequiredError() {
    const metadataname: string = this.getBottomMetadataName();

    return (
      this.metadata && metadataname === 'editableId' && this.typeValue && this.typeValue === 'dynamic-editable' && !this.textField.value
    );
  }
  public isIntegerRequiredError() {
    const metadataname: string = this.getBottomMetadataName();

    return (
      this.metadata && metadataname === 'maxLength' && this.typeValue && this.typeValue === 'dynamic-editable' && !this.integerField.value
    );
  }
  public isSelectRequiredError() {
    const metadataname: string = this.getBottomMetadataName();
    return (
      this.metadata &&
      metadataname === 'format' &&
      this.dynamicIdValue &&
      (this.dynamicIdValue === 'pageNumber' ||
        this.dynamicIdValue === 'inspection[0].inspectionDate' ||
        this.dynamicIdValue === 'system.now') &&
      !this.selectField.value
    );
  }

  // editableIdの入力文字チェック
  public isIllegalError(): boolean {
    const metadataname: string = this.getBottomMetadataName();

    return (
      this.isVisol &&
      this.metadata &&
      metadataname === 'editableId' &&
      this.typeValue &&
      this.typeValue === 'dynamic-editable' &&
      this.textField.value &&
      !this.designService.isLowerCamelCase(this.textField.value)
    );
  }

  public onChangeAtSelect() {
    if (this.selectField.hasError('required')) {
      return;
    }
    switch (this.selectField.value) {
      case 'visualEvents':
        this.designService.selectProperties = this.designService.visualEventPropertyList;
        this.designService.selectLabel = 'Visual Events';
        break;
      case 'signature':
        this.designService.selectProperties = this.designService.signaturePropertyList;
        this.designService.selectLabel = 'Signature';
        break;
      case 'notes':
        this.designService.selectProperties = this.designService.notesPropertyList;
        this.designService.selectLabel = 'notes';
        break;
    }
    if (this.oldValidValue !== this.selectField.value) {
      this.changeValue.emit({
        id: this.propId,
        name: this.metadata.name,
        parentName: this.metadata.parentName,
        value: this.selectField.value,
        oldValue: this.oldValidValue,
      });
      this.oldValidValue = this.selectField.value;
    }
  }

  public onChangeAtCheckBox(event: MatCheckboxChange) {
    this.changeValue.emit({
      id: this.propId,
      name: this.metadata.name,
      parentName: this.metadata.parentName,
      value: event.checked,
      oldValue: this.oldValidValue,
    });

    this.checkBoxField.setValue(event.checked);

    this.oldValidValue = this.checkBoxField.value;
  }
  public onChangeType(type: string) {
    const metadataname: string = this.getBottomMetadataName();

    if (this.metadata && (metadataname === 'dynamicDataField' || metadataname === 'dynamicId')) {
      if (type === 'static') {
        this.metadata.isDisplay = false;
      } else {
        this.metadata.isDisplay = true;
      }
    }
    if (this.metadata && metadataname === 'editableId') {
      if (type === 'static' || type === 'dynamic') {
        this.metadata.isDisplay = false;
        this.metadata.value = null;
      } else {
        this.metadata.isDisplay = true;
      }
    }
    if (this.metadata && metadataname === 'text') {
      if (type === 'dynamic') {
        this.metadata.isDisplay = false;
      } else {
        this.metadata.isDisplay = true;
      }
    }
    if (this.metadata && metadataname === 'src') {
      if (type === 'dynamic') {
        this.metadata.isDisplay = false;
      } else {
        this.metadata.isDisplay = true;
      }
    }
    if (this.metadata && metadataname === 'inputType') {
      if (type === 'dynamic' || type === 'static') {
        this.metadata.isDisplay = false;
        this.metadata.value = 'text';
        this.inputTypeValue = this.metadata.value;
      } else if (type === 'dynamic-editable') {
        this.metadata.isDisplay = true;
      }
    }
    if (this.metadata && (metadataname === 'maxLength' || metadataname === 'required')) {
      if (type === 'dynamic-editable') {
        this.metadata.isDisplay = true;
      } else {
        this.metadata.isDisplay = false;
      }
    }
    if (this.metadata && metadataname === 'format') {
      if (
        (type === 'dynamic' || type === 'dynamic-editable') &&
        (this.dynamicIdValue === 'pageNumber' ||
          this.dynamicIdValue === 'inspection[0].inspectionDate' ||
          this.dynamicIdValue === 'system.now')
      ) {
        this.metadata.isDisplay = true;
      } else {
        this.metadata.isDisplay = false;
      }
    }
    if (this.metadata && (metadataname === 'label' || metadataname === 'inputGroup')) {
      if (type === 'dynamic-editable') {
        this.metadata.isDisplay = true;
      } else {
        this.metadata.isDisplay = false;
      }
    }
    if (this.metadata && metadataname === 'options' && this.inputTypeValue) {
      if (type === 'dynamic-editable' && (this.inputTypeValue === 'select' || this.inputTypeValue === 'multiSelect')) {
        this.metadata.isDisplay = true;
      } else {
        this.metadata.isDisplay = false;
        this.isMultiEdit = false;
      }
    }
    if (this.metadata && metadataname === 'multiSelectDelimiter') {
      if (type === 'dynamic-editable' && this.inputTypeValue === 'multiSelect') {
        this.metadata.isDisplay = true;
        this.metadata.isRequired = true;
      } else {
        this.metadata.isDisplay = false;
        this.metadata.isRequired = false;
      }
    }
    if (
      this.metadata &&
      (metadataname === 'radioType' || metadataname === 'radioLabel' || metadataname === 'value') &&
      this.inputTypeValue === 'radio'
    ) {
      if (type === 'dynamic-editable') {
        this.metadata.isDisplay = true;
        this.metadata.isRequired = true;
      } else {
        this.metadata.isDisplay = false;
        this.metadata.isRequired = false;
        this.metadata.value = null;
      }
    }
  }
  public onChangeInputType(inputType: string) {
    if (this.typeValue !== 'dynamic-editable') {
      return;
    }

    const metadataname: string = this.getBottomMetadataName();

    if (this.metadata && metadataname === 'options') {
      if (inputType !== 'select' && inputType !== 'multiSelect') {
        this.metadata.isDisplay = false;
        this.isMultiEdit = false;
        this.metadata.value = '[]';
      } else {
        this.metadata.isDisplay = true;
        this.isMultiEdit = true;
        const options: Array<TypeOfJSON> = this.metadata.value as Array<TypeOfJSON>;
        if (!this.metadata.value || this.metadata.value === '[]' || !options || options.length === 0) {
          this.isOptionsError = true;
        } else {
          this.isOptionsError = false;
        }
      }
    } else if (this.metadata && metadataname === 'multiSelectDelimiter') {
      if (inputType !== 'multiSelect') {
        this.metadata.isDisplay = false;
        this.metadata.isRequired = false;
      } else {
        this.metadata.isDisplay = true;
        this.metadata.isRequired = true;
      }
    } else if (this.metadata && (metadataname === 'radioType' || metadataname === 'radioLabel' || metadataname === 'value')) {
      if (inputType !== 'radio') {
        this.metadata.isDisplay = false;
        this.metadata.isRequired = false;
      } else {
        this.metadata.isDisplay = true;
        this.metadata.isRequired = true;
      }
    }
  }
  public onChangeDynamicId(dynamicId: string) {
    const metadataname: string = this.getBottomMetadataName();

    if (this.selectField && this.metadata && metadataname === 'format') {
      const temp = this.metadata.value ? this.metadata.value : null;
      if (dynamicId === 'pageNumber') {
        this.metadata.isDisplay = true;
        if (PROP_DYNAMIC_PAGEFORMAT.some(_ => _.value === temp)) {
          this.selectField.setValue(temp);
        } else {
          this.selectField.setValue(null);
        }
        this.metadata.selectItems = PROP_DYNAMIC_PAGEFORMAT;
        this.metadata.isRequired = true;
      } else if (dynamicId === 'inspection[0].inspectionDate' || dynamicId === 'system.now') {
        this.metadata.isDisplay = true;
        if (PROP_DYNAMIC_DATEFORMAT.some(_ => _.value === temp)) {
          this.selectField.setValue(temp);
        } else {
          this.selectField.setValue(null);
        }
        this.metadata.selectItems = PROP_DYNAMIC_DATEFORMAT;
        this.metadata.isRequired = true;
      } else {
        this.metadata.isDisplay = false;
        this.selectField.setValue(null);
        this.metadata.isRequired = false;
      }
      this.changeValue.emit({
        id: this.propId,
        name: this.metadata.name,
        parentName: this.metadata.parentName,
        value: this.selectField.value,
        oldValue: this.oldValidValue,
      });
    }
  }

  public getBottomMetadataName() {
    const nameList = this.metadata.name.split('.');
    return nameList[nameList.length - 1];
  }

  public addListItem(metadata): void {
    if (metadata.name === 'columns') {
      this.listItem.push({ label: this.translate.instant('objects.Header#'), dataField: '', widthRatio: 5 });
    } else {
      this.listItem.push({ label: '', dataField: '' });
    }
    this.onChangeAtList();
  }

  public getLabel(value: string): string {
    return this.translate.instant('objects.' + value).replace('objects.', '');
  }

  public addPriorityItem(): void {
    this.priorityItems = this.priorityItems ? this.priorityItems : [];
    this.priorityItems.push({ code: this.translate.instant('objects.Priority#'), label: '' });
    this.priorityItemsDataSource.data = this.priorityItems;
    this.onChangeAtPriorityItems();
  }

  public addDropDownOptionItem(): void {
    this.dropDownOptionItems = this.dropDownOptionItems ? this.dropDownOptionItems : [];
    this.dropDownOptionItems.push({ label: '', value: this.generateUUID() });
    this.onChangeAtDropDownOptionItems();
  }

  public onChangeAtPriorityItems() {
    this.metadata.value = this.priorityItems;
    const tmpValue = JSON.stringify(this.priorityItems, undefined, 4);
    if (this.oldValidValue !== tmpValue) {
      this.changeValue.emit({
        id: this.propId,
        name: this.metadata.name,
        parentName: this.metadata.parentName,
        value: this.priorityItems,
        oldValue: this.oldValidValue,
      });
      this.oldValidValue = tmpValue;
    }
  }

  public onChangeAtDropDownOptionItems() {
    this.metadata.value = this.dropDownOptionItems;
    const tmpValue = JSON.stringify(this.dropDownOptionItems, undefined, 4);
    if (this.oldValidValue !== tmpValue) {
      this.changeValue.emit({
        id: this.propId,
        name: this.metadata.name,
        parentName: this.metadata.parentName,
        value: this.dropDownOptionItems,
        oldValue: this.oldValidValue,
      });
      this.oldValidValue = tmpValue;
    }
  }

  public removeDropDownOptionItems(index: number): void {
    this.dropDownOptionItems.splice(index, 1);
    this.onChangeAtDropDownOptionItems();
  }

  public removePriorityItems(index: number): void {
    this.priorityItems.splice(index, 1);
    this.priorityItemsDataSource.data = this.priorityItems;
    this.onChangeAtPriorityItems();
  }

  public removeColumn(index: number): void {
    this.listItem.splice(index, 1);
    this.onChangeAtList();
  }

  public addTableHeader(metaDataName: string, event: MatChipInputEvent): void {
    if (metaDataName === 'columnList' && (event.value || '').trim()) {
      this.tableColumnHeader.push({
        label: event.value.trim(),
        width: 10,
        inputType: '',
        textOrientation: '',
      } as ColumnList);
    } else if (metaDataName === 'rowList' && (event.value || '').trim()) {
      this.tableRowHeader.push({
        label: event.value.trim().slice(0, this.maxVeLabelLength),  // For some reason the max length is not working in the template
        isCategory: false,
      } as RowList);
    }
    if (event.input) {
      event.input.value = '';
    }
    this.onChangeAtTableHeader();
  }

  public removeTableHeader(metaDataName: string, index: number): void {
    this.listItem.splice(index, 1);
    if (metaDataName === 'columnList') {
      this.tableColumnHeader.splice(index, 1);
    } else if (metaDataName === 'rowList') {
      this.tableRowHeader.splice(index, 1);
    }
    this.onChangeAtTableHeader();
  }

  public addOption(item: ListItem, event: MatChipInputEvent): void {
    if (!event.value?.trim()) {
      return;
    }
    if (item.optionList && item.optionList.length > 0) {
      item.optionList.push(event.value?.trim());
    } else {
      item.optionList = [event.value?.trim()];
    }
    if (event.input) {
      event.input.value = '';
    }
    this.onChangeAtList();
  }

  public removeOption(item: ListItem, index: number): void {
    item.optionList.splice(index, 1);
    this.onChangeAtList();
  }

  public addTableColumn(metaDataName: string): void {
    if (metaDataName === 'columns') {
      this.tableColumns.push({
        header: this.translate.instant('propertypreviewEditor.newColumn'),
        headerType: null,
        texts: [],
        textsInfo: [],
        fontSize: null,
        fontFamily: null,
        horizontalAlign: null,
        verticalAlign: null,
        padding: null,
        paddingBottom: null,
        paddingLeft: null,
        paddingRight: null,
        paddingTop: null,
        widthRatio: 0,
        isHeaderVertical: false,
        isColumnVertical: false,
        isCombined: false,
        isItemsHorizontal: false,
      });
    }

    this.onChangeAtTableColumn();
  }

  public removeTableColumn(metaDataName: string, index: number): void {
    this.listItem.splice(index, 1);
    if (metaDataName === 'columns') {
      this.tableColumns.splice(index, 1);
    }
    this.onChangeAtTableColumn();
  }

  /**
   * テーブルカラムを移動する。
   *
   * @param event イベントデータ
   */
  public moveTableColumn(event: CdkDragDrop<Array<DisplayTableColumn>>): void {
    moveItemInArray(this.tableColumns, event.previousIndex, event.currentIndex);
    this.onChangeAtTableColumn();
  }

  public getPropertyName(metadata: any) {
    if (metadata.isFieldType('DropDownOptionItems')) {
      return 'options';
    } else {
      return metadata.name;
    }
  }
  public dropTable(event: CdkDragDrop<Options[]>) {
    const prevIndex = this.optionDataSource.data.findIndex(d => d === event.item.data);
    moveItemInArray(this.optionDataSource.data, prevIndex, event.currentIndex);
    this.dragDropTable.renderRows();
    this.multiField = this.optionDataSource.data;
    this.onChangeAtMulti();
    console.log('optionDataSource', this.optionDataSource);
  }

  public priorityDropTable(event: CdkDragDrop<PriorityList[]>) {
    const prevIndex = this.priorityItemsDataSource.data.findIndex(d => d === event.item.data);
    moveItemInArray(this.priorityItemsDataSource.data, prevIndex, event.currentIndex);
    this.priorityItemspTable.renderRows();
    this.priorityItems = this.priorityItemsDataSource.data;
    this.onChangeAtPriorityItems();
  }

  public listTable(event: CdkDragDrop<ListItem[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      this.listItem = event.container.data;
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
      this.listItem = event.container.data;
    }
    this.onChangeAtList();
  }

  /**
   * テーブルカラムのパネルを全て開く。
   */
  public expandAllTableColumns(): void {
    this.tableColumnAccordion?.openAll();
  }

  /**
   * テーブルカラムのパネルを全て閉じる。
   */
  public collapseAllTableColumns(): void {
    this.tableColumnAccordion?.closeAll();
  }

  public getTypeOptions(): { label: string; value: string }[] {
    let type: string;
    const selectedElemProps: ReportProperties[] = this.designService.getSelectedElementProperties();
    selectedElemProps.forEach((reportProperties: ReportProperties) => {
      type = this.getPanelType(reportProperties.getPropOrElemType());
    });

    switch (type) {
      case ElementType.Summary:
        return TEXT_TYPES;
      case ElementType.Picture:
      case ElementType.SubTotal:
        return [
          { label: 'static', value: 'static' },
          { label: 'dynamic', value: 'dynamic' },
        ];
    }
  }

  public getDisplayOptions(value?: string): { label: string; value: string }[] {
    if (value === 'header') {
      return HEADER_DYNAMIC_SELECT_ITEM;
    }

    let type: string;
    const selectedElemProps: ReportProperties[] = this.designService.getSelectedElementProperties();
    selectedElemProps.forEach((reportProperties: ReportProperties) => {
      type = this.getPanelType(reportProperties.getPropOrElemType());
    });

    switch (type) {
      case ElementType.Summary:
        return SUMMARY_DYNAMIC_SELECT_ITEM;
      case ElementType.Picture:
        return PICTURES_DYNAMIC_SELECT_ITEM;
      case ElementType.SubTotal:
        return SUBTOTAL_DYNAMIC_SELECT_ITEM;
    }
  }

  public getPanelType(title: string): string {
    const types: ElementType[] = [
      ElementType.Page,
      ElementType.Text,
      ElementType.Line,
      ElementType.Image,
      ElementType.Rectangle,
      ElementType.Summary,
      ElementType.SubTotal,
      ElementType.Picture,
      ElementType.DataPlate,
      ElementType.MeasurementPoint,
      ElementType.VisualEvents,
      ElementType.Signature,
      ElementType.Section,
      ElementType.Table,
      ElementType.InteractiveIso,
      ElementType.DropDownList,
      ElementType.CustomElement,
    ];
    for (const type of types) {
      if (title.indexOf(type) === 0) {
        return type;
      }
    }
    return null;
  }

  public deleteColumn(name: string, i1: number, headerName: string): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(CommonDeleteDialogComponent, {
      width: '600px',
      minWidth: '400px',
      maxHeight: '800px',
      data: { target: this.translate.instant('propertypreviewEditor.column'), deleteNames: [headerName] },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.removeTableColumn(name, i1);
      }
    });
  }

  public deleteItem(i1: number, i2: number): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(CommonDeleteDialogComponent, {
      width: '600px',
      minWidth: '400px',
      maxHeight: '800px',
      data: { target: this.translate.instant('propertypreviewEditor.item'), deleteNames: ['item' + (i2 + 1)] },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deleteText(i1, i2);
      }
    });
  }

  //Delete確認ダイアログ
  public deleteOption(i: number, optionName: string): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(CommonDeleteDialogComponent, {
      width: '600px',
      minWidth: '400px',
      maxHeight: '800px',
      data: { target: this.translate.instant('propertypreviewEditor.option'), deleteNames: [optionName] },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deleteObject(i);
      }
    });
  }

  //Delete確認ダイアログ
  public deletePriority(i: number, priorityName: any): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(CommonDeleteDialogComponent, {
      width: '600px',
      minWidth: '400px',
      maxHeight: '800px',
      data: { target: this.translate.instant('propertypreviewEditor.priority'), deleteNames: [priorityName] },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.removePriorityItems(i);
      }
    });
  }

  //Delete確認ダイアログ
  public deleteLabel(i: number, itemLabel: any): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(CommonDeleteDialogComponent, {
      width: '600px',
      minWidth: '400px',
      maxHeight: '800px',
      data: { target: this.translate.instant('propertypreviewEditor.column'), deleteNames: [itemLabel] },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.removeColumn(i);
      }
    });
  }

  //Delete確認ダイアログ
  public deleteDropDownOption(i: number, itemLabel: any): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(CommonDeleteDialogComponent, {
      width: '600px',
      minWidth: '400px',
      maxHeight: '800px',
      data: { target: this.translate.instant('propertypreviewEditor.option'), deleteNames: [itemLabel[i].label] },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.removeDropDownOptionItems(i);
      }
    });
  }

  private generateUUID(): string {
    const chars = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.split('');
    for (let i = 0, len = chars.length; i < len; i++) {
      switch (chars[i]) {
        case 'x':
          chars[i] = Math.floor(Math.random() * 16).toString(16);
          break;
        case 'y':
          chars[i] = (Math.floor(Math.random() * 4) + 8).toString(16);
          break;
      }
    }
    return chars.join('');
  }

  private tryParseJson(value: string): any {
    try {
      return JSON.parse(value);
    } catch (error) {
      return value;
    }
  }
}
